import { defineStore } from 'pinia';
import axios from 'axios';
import authApi from '@/utils/auth-api';

export interface AuthUser {
  id: number | string;
  email: string;
  name?: string;
}

const TOKEN_KEY = 'authToken';
const USER_KEY = 'authUser';

// Separate from useApi to avoid a store <-> composable import cycle.
const backend = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
  timeout: 10000,
});

function readCachedUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem(TOKEN_KEY) as string | null,
    user: readCachedUser(),
    loading: false,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    currentUser: (state) => state.user,
  },

  actions: {
    setSession(token: string, user: AuthUser | null) {
      this.token = token;
      this.user = user;
      localStorage.setItem(TOKEN_KEY, token);
      if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(USER_KEY);
      }
    },

    async login(email: string, password: string) {
      this.loading = true;
      try {
        const { data } = await authApi.post('/auth/login', { email, password });
        if (!data?.token) {
          throw new Error('Login response did not include a token');
        }
        this.setSession(data.token, data.user ?? null);
        return data;
      } finally {
        this.loading = false;
      }
    },

    async loginWithGoogle(idToken: string) {
      this.loading = true;
      try {
        const { data } = await authApi.post('/auth/login-google', { idToken });
        if (!data?.token) {
          throw new Error('Google login response did not include a token');
        }
        this.setSession(data.token, data.user ?? null);
        return data;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Asks the backend (which asks io_auth) whether the stored token is still valid.
     * Returns false only when the token was explicitly rejected; network errors and
     * 5xx keep the session so a blip does not log the user out.
     */
    async verifySession(): Promise<boolean> {
      if (!this.token) return false;
      this.loading = true;
      try {
        const { data } = await backend.get('/auth/verify_session', {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        const user = data?.data?.user as AuthUser | undefined;
        if (user) {
          this.setSession(this.token, user);
        }
        return true;
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (status === 401 || status === 403) {
          this.logout();
          return false;
        }
        console.warn('verify_session could not be completed; keeping session', error);
        return true;
      } finally {
        this.loading = false;
      }
    },

    logout() {
      this.user = null;
      this.token = null;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    },
  },
});
